import { useState } from "react";
import {
  fetchSignaturePadTokenStatus,
  issueSignaturePadToken,
} from "../../api/crm";
import { useToast } from "../common/Toast";
import PersistentQrModal from "./PersistentQrModal";

const signaturePadUrl = (token: string) =>
  `${window.location.origin}/signature-pad/${token}`;

function useSignaturePadConnection() {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState<string | null>(null);

  const loadUrl = async (): Promise<string> => {
    const status = await fetchSignaturePadTokenStatus();
    const token =
      status.active && status.token
        ? status.token
        : (await issueSignaturePadToken()).token;
    const next = signaturePadUrl(token);
    setUrl(next);
    return next;
  };

  const openPad = async () => {
    if (busy) return;
    // 네트워크 응답 뒤 window.open을 부르면 브라우저가 사용자 클릭으로 보지 않아 팝업을 막는다.
    // 빈 창을 먼저 잡고 주소 조회 뒤 이동시킨다.
    const win = window.open("about:blank", "_blank");
    if (!win) {
      toast.error("팝업이 차단됐습니다. 📱 태블릿 연결에서 주소를 복사해 주세요.");
      return;
    }
    win.opener = null;
    setBusy(true);
    try {
      win.location.replace(await loadUrl());
    } catch (err) {
      win.close();
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`서명 패드를 열지 못했습니다: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  const openConnection = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await loadUrl();
      setOpen(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`태블릿 연결 주소를 불러오지 못했습니다: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  const copyAddress = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("태블릿 연결 주소를 복사했습니다.");
    } catch {
      toast.error("주소를 복사하지 못했습니다. 위 주소를 길게 눌러 복사해 주세요.");
    }
  };

  const reissue = async () => {
    if (
      !window.confirm(
        "새로 발급하면 지금 연결된 태블릿과 기존 주소가 즉시 끊깁니다.\n계속할까요?",
      )
    ) {
      return;
    }
    try {
      const issued = await issueSignaturePadToken();
      setUrl(signaturePadUrl(issued.token));
      toast.success("태블릿 연결 주소를 새로 발급했습니다. 새 QR을 다시 찍어 주세요.");
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`태블릿 연결 주소 재발급 실패: ${msg}`);
    }
  };

  return {
    busy,
    open,
    url,
    setOpen,
    openPad,
    openConnection,
    copyAddress,
    reissue,
  };
}

type SignaturePadConnection = ReturnType<typeof useSignaturePadConnection>;

function SignaturePadConnectionModal({
  connection,
  layer = "base",
  escMode = "direct",
}: {
  connection: SignaturePadConnection;
  layer?: "base" | "elevated";
  escMode?: "direct" | "stack";
}) {
  return (
    <PersistentQrModal
      isOpen={connection.open}
      onClose={() => connection.setOpen(false)}
      title="태블릿 연결"
      subtitle="태블릿으로 QR을 한 번 찍어 두면 로그인 없이 서명 대기 화면이 계속 열립니다."
      url={connection.url}
      layer={layer}
      escMode={escMode}
      actions={[
        {
          label: "📋 복사",
          onClick: connection.copyAddress,
          tone: "outline",
          disabled: connection.url == null,
        },
        {
          label: "🔄 재발급",
          onClick: connection.reissue,
          tone: "outline",
          loadingLabel: "발급 중…",
        },
      ]}
    />
  );
}

/** 서명 칸처럼 작은 버튼만 필요한 자리에서도 상단과 같은 QR 연결 흐름을 쓴다. */
export function SignaturePadConnectionButton({
  className = "",
}: {
  className?: string;
}) {
  const connection = useSignaturePadConnection();

  return (
    <>
      <button
        type="button"
        onClick={connection.openConnection}
        disabled={connection.busy}
        className={className}
        title="태블릿에서 로그인 없이 열 고정 QR과 주소를 띄웁니다"
        data-signature-pad-connect
      >
        {connection.busy ? "불러오는 중…" : "📱 태블릿 연결"}
      </button>
      <SignaturePadConnectionModal
        connection={connection}
        layer="elevated"
        escMode="stack"
      />
    </>
  );
}

export default function SignaturePadButton({ className = "" }: { className?: string }) {
  const connection = useSignaturePadConnection();

  return (
    <>
      <div className="flex shrink-0 items-center">
        <button
          type="button"
          onClick={connection.openPad}
          disabled={connection.busy}
          className={`${className} rounded-l-lg rounded-r-none disabled:opacity-60`}
          title="현재 매장의 서명 전용 화면을 엽니다"
        >
          ✍️ 서명 패드 열기
        </button>
        <button
          type="button"
          onClick={connection.openConnection}
          disabled={connection.busy}
          className="flex h-9 items-center whitespace-nowrap rounded-r-lg border border-l-0 border-brand-500 px-2 text-[11px] font-semibold text-brand-500 transition hover:bg-brand-50 disabled:opacity-60 dark:hover:bg-white/[0.03]"
          title="태블릿에서 로그인 없이 열 고정 QR과 주소를 띄웁니다"
        >
          {connection.busy ? "불러오는 중…" : "📱 태블릿 연결"}
        </button>
      </div>

      <SignaturePadConnectionModal connection={connection} />
    </>
  );
}
